import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  datasets: [],
  sqlEnabled: false,

  selectedDataset: null,
  availableFields: [],
  pagination: {
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0,
  },
  query: "",
  queryResult: [],

  // ── 1.1 Header Setup (single source of truth: ds_output_column) ───────────
  // Replaces the old tabConfig / isModify / field-loader state.
  //
  // Shape mirrors HeaderSetupResponse:
  //   { datasetId: number|null, tabs: HeaderTabResponse[] }
  //
  // HeaderTabResponse: { tabId, name, tabOrder, fields: HeaderFieldItem[] }
  // HeaderFieldItem:   { id, name, order }
  //
  // FieldsSetup and FieldMapper both read from here — it is the single source
  // of truth for what headers exist per tab.
  headerSetup: {
    datasetId: null,
    tabs: [],
  },

  // True when the user has made local edits in Header Setup that have not yet
  // been persisted via PUT /header-setup.
  isHeaderModified: false,

  // ── 1.2 Map Fields – output columns, keyed by tabId (string) ─────────────
  // Allows multiple tabs to hold unsaved state simultaneously.
  // { [tabId]: OutputColumnResponse[] }
  outputColumnsByTab: {},

  // { [tabId]: ValidationResponse }
  outputValidationByTab: {},

  // { [tabId]: AutoMatchResponse }
  autoMatchResultByTab: {},

  // ── 1.3 Output Preview ────────────────────────────────────────────────────
  outputResult: null,
  outputLoading: false,
  outputError: null,

  // ── Export Format ─────────────────────────────────────────────────────────
  // Derived on fetch from whichever config is present on the dataset.
  // null  → user hasn't chosen yet (show format picker)
  // "EXCEL" → DsReportDesign exists
  // "CSV"   → DsCsvExportConfig exists
  exportFormat: null,

  // ── Excel / Report Design ─────────────────────────────────────────────────
  reportDesign: {
    fileName: "",
    exportStyleId: "",
    exportStyle: null,
    tabs: [],
  },

  // ── CSV Export Config ─────────────────────────────────────────────────────
  // Mirrors CsvExportConfigResponse shape.
  csvExportConfig: {
    datasetId: null,
    datasetName: "",
    folderName: "",
    delimiter: "COMMA",
    encoding: "UTF_8",
    includeHeader: true,
    quoteAllFields: false,
    tabs: [],           // [{ tabId, tabName, outputFileName }]
    createdAt: null,
    createdBy: null,
    updatedAt: null,
    updatedBy: null,
  },

  loading: false,
};

const datasetSlice = createSlice({
  name: "dataset",
  initialState,
  reducers: {
    setLoading(state, action) {
      state.loading = action.payload;
    },

    setDatasets(state, action) {
      state.datasets = action.payload;
    },

    setSelectedDataset(state, action) {
      state.selectedDataset = action.payload;
      state.query = "";
      state.queryResult = [];
      // Clear all per-tab output state when switching datasets
      state.outputColumnsByTab = {};
      state.outputValidationByTab = {};
      state.autoMatchResultByTab = {};
      state.outputResult = null;
      state.outputError = null;
      // Clear header setup for the new dataset
      state.headerSetup = { datasetId: null, tabs: [] };
      state.isHeaderModified = false;
      // Reset export state on dataset switch
      state.exportFormat = null;
      state.reportDesign = initialState.reportDesign;
      state.csvExportConfig = initialState.csvExportConfig;
    },

    setStatus(state, action) {
      state.selectedDataset.status = action.payload;
    },

    setPagination(state, action) {
      state.pagination = action.payload;
    },

    setAvailableFields(state, action) {
      state.availableFields = action.payload;
    },

    setQuery(state, action) {
      state.query = action.payload;
    },

    setQueryResults(state, action) {
      state.queryResult = action.payload;
    },

    setSqlEnabled(state, action) {
      state.sqlEnabled = action.payload;
    },

    // ── 1.1 Header Setup ──────────────────────────────────────────────────────

    /**
     * Replace the entire header setup state from a HeaderSetupResponse.
     * payload: { datasetId, tabs: [{ tabId, name, tabOrder, fields: [{ id, name, order }] }] }
     */
    setHeaderSetup(state, action) {
      state.headerSetup = {
        datasetId: action.payload.datasetId ?? null,
        tabs: action.payload.tabs ?? [],
      };
    },

    /**
     * Apply a local (unsaved) edit to headerSetup.tabs without hitting the API.
     * Sets isHeaderModified = true to activate the Save button in FieldsSetup.
     * payload: HeaderTabResponse[] — the full updated tabs array
     */
    setHeaderSetupLocal(state, action) {
      state.headerSetup = {
        ...state.headerSetup,
        tabs: action.payload,
      };
      state.isHeaderModified = true;
    },

    /**
     * Flip the dirty flag.
     * Set to false after a successful PUT /header-setup or after reset.
     */
    setHeaderModified(state, action) {
      state.isHeaderModified = action.payload;
    },

    // ── 1.2 Map Fields — per-tab ──────────────────────────────────────────────

    /** payload: { tabId: string|number, columns: OutputColumnResponse[] } */
    setTabOutputColumns(state, action) {
      const { tabId, columns } = action.payload;
      state.outputColumnsByTab[String(tabId)] = columns;
    },

    /** payload: { tabId: string|number, validation: ValidationResponse } */
    setTabOutputColumnsValidation(state, action) {
      const { tabId, validation } = action.payload;
      state.outputValidationByTab[String(tabId)] = validation;
    },

    /** payload: { tabId: string|number, result: AutoMatchResponse } */
    setTabAutoMatchResult(state, action) {
      const { tabId, result } = action.payload;
      state.autoMatchResultByTab[String(tabId)] = result;
    },

    /** Clear all output-column state for a single tab (e.g. after reset) */
    clearTabOutputState(state, action) {
      const tabId = String(action.payload);
      delete state.outputColumnsByTab[tabId];
      delete state.outputValidationByTab[tabId];
      delete state.autoMatchResultByTab[tabId];
    },

    // ── 1.3 Output Preview ────────────────────────────────────────────────────

    setOutputLoading(state, action) {
      state.outputLoading = action.payload;
    },

    setOutputResult(state, action) {
      state.outputResult = action.payload;
    },

    setOutputError(state, action) {
      state.outputError = action.payload;
    },

    // ── Export Format ─────────────────────────────────────────────────────────

    /**
     * Set the active export format: "EXCEL" | "CSV" | null.
     * Called when user explicitly picks a format, or derived from fetch.
     */
    setExportFormat(state, action) {
      state.exportFormat = action.payload;
    },

    // ── Excel / Report Design ─────────────────────────────────────────────────

    setReportDesignDraft(state, action) {
      state.reportDesign = {
        ...state.reportDesign,
        ...action.payload,
      };
    },

    setReportConfig(state, action) {
      const { fileName, exportStyle, exportStyleId, tabs } = action.payload;
      state.reportDesign.fileName = fileName || "";
      state.reportDesign.exportStyleId = exportStyleId || null;
      state.reportDesign.exportStyle = exportStyle || null;
      state.reportDesign.tabs = (tabs || []).map((tab) => ({
        ...tab,
        tabId: tab.tabId || tab.id,
        exportStyleId:
            tab.exportStyleId ?? tab.exportStyle?.id ?? tab.exportStyle ?? null,
      }));
    },

    // ── CSV Export Config ─────────────────────────────────────────────────────

    /**
     * Populate csvExportConfig from CsvExportConfigResponse (after GET or PUT).
     */
    setCsvExportConfig(state, action) {
      const payload = action.payload;
      state.csvExportConfig = {
        datasetId:    payload.datasetId    ?? null,
        datasetName:  payload.datasetName  ?? "",
        folderName:   payload.folderName   ?? "",
        delimiter:    payload.delimiter    ?? "COMMA",
        encoding:     payload.encoding     ?? "UTF_8",
        includeHeader:  payload.includeHeader  ?? true,
        quoteAllFields: payload.quoteAllFields ?? false,
        tabs:         payload.tabs         ?? [],
        createdAt:    payload.createdAt    ?? null,
        createdBy:    payload.createdBy    ?? null,
        updatedAt:    payload.updatedAt    ?? null,
        updatedBy:    payload.updatedBy    ?? null,
      };
    },

    /**
     * Local-only draft update for CSV config — no API call.
     * Merges partial changes so individual field edits don't blow away siblings.
     */
    setCsvConfigDraft(state, action) {
      state.csvExportConfig = {
        ...state.csvExportConfig,
        ...action.payload,
      };
    },
  },
});

export const datasetAction = datasetSlice.actions;
export const datasetReducer = datasetSlice.reducer;